import type { FeatureReferenceBinding } from '../../feature/binding/FeatureReferenceBinding';

/**
 * 归一化草图 ID。
 *
 * @param sketchId 候选草图 ID。
 * @returns 有效草图 ID；无效时返回 null。
 */
function normalizeSketchId(sketchId: string | null | undefined): string | null {
    if (typeof sketchId !== 'string') {
        return null;
    }
    const trimmedSketchId = sketchId.trim();
    return trimmedSketchId.length > 0 ? trimmedSketchId : null;
}

/**
 * 从 profile 特征定义与输入绑定中解析来源草图。
 *
 * 设计说明：
 * 1. 拉伸和旋转都属于草图 profile 驱动特征，来源草图不能分散在 adapter、preview、commit 各自解析；
 * 2. 来源草图属于特征输入引用，唯一业务真相是 `sketch-profile` 绑定中的草图持久 ID；
 *    `definition.sketchId` 只是特征创建当时的运行时 uuid，文档重开后该 uuid 已失效，
 *    因此只能作为“尚未生成绑定”的中间态命令与历史命令的兜底，绝不能反过来盖住绑定；
 * 3. 回退控制棒、Undo/Redo 和设计树投影都消费 prototype 的 sourceSketchId，因此这里必须成为统一入口。
 *
 * @param input 解析输入。
 * @returns 来源草图 ID；无法确认时返回 null。
 */
export function resolveSketchProfileSourceSketchId(input: {
    definitionSourceSketchId: string | null | undefined;
    bindings: readonly FeatureReferenceBinding[];
}): string | null {
    for (const binding of input.bindings) {
        if (binding.kind !== 'sketch-profile') {
            continue;
        }
        const payload = binding.serialize();
        if (payload.kind !== 'sketch-profile') {
            continue;
        }
        const bindingSketchId = normalizeSketchId(payload.sketchId);
        if (bindingSketchId) {
            return bindingSketchId;
        }
    }

    return normalizeSketchId(input.definitionSourceSketchId);
}
